// src/utils/warehouseEvents.js
/**
 * Centralized event logging for warehouse inventory changes.
 * All inventory modifications should flow through these functions.
 */

import { addDoc, collection, serverTimestamp } from "firebase/firestore"
import { db } from "../../../../firebase/config"

/**
 * Event types for warehouse operations
 */
export const EVENT_TYPES = {
  INCOMING: "incoming",
  OUTGOING: "outgoing",
  ROLL_CREATED: "roll_created",
  ROLL_CONSUMED: "roll_consumed",
  ROLL_MOVED: "roll_moved",
  INVENTORY_ADJUSTMENT: "inventory_adjustment",
  INVENTORY_CREATED: "inventory_created",
  INVENTORY_DELETED: "inventory_deleted",
  ROLL_DELETED: "roll_deleted",
}

/**
 * Log a warehouse event
 * @param {Object} params Event parameters
 * @returns {Promise<DocumentReference>}
 */
export async function logWarehouseEvent({
  eventType,
  timestamp = new Date(),
  productTypeId = null,
  productTypeName = null,
  productName = null,
  materialId = null,
  materialName = null,
  unit = null,
  quantityChange = 0, // positive for incoming, negative for outgoing
  location = null,
  rollWidth = null,
  barcode = null,
  ean = null,
  batchSeries = null,
  price = null,
  total = null,
  metadata = {},
}) {
  const event = {
    eventType,
    timestamp,
    createdAt: serverTimestamp(),

    // Product identification
    productTypeId,
    productTypeName,
    productName,

    // Material info
    materialId,
    materialName,

    // Quantity & unit
    unit,
    quantityChange: Number(quantityChange) || 0,

    // Location
    location,

    // Roll-specific
    rollWidth: rollWidth ? Number(rollWidth) : null,

    // Identifiers
    barcode: barcode || null,
    ean: ean || null,
    batchSeries: batchSeries || null,

    // Financial
    price: price ? Number(price) : null,
    total: total ? Number(total) : null,

    // Extensible metadata
    metadata: {
      sourceCollection: null,
      sourceDocId: null,
      supplierId: null,
      supplierName: null,
      orderId: null,
      userId: null,
      notes: null,
      ...metadata,
    },
  }

  return await addDoc(collection(db, "warehouseEvents"), event)
}

/**
 * Log incoming items event
 */
export async function logIncomingEvent({
  row,
  arrivedAt,
  productTypeId,
  productTypeName,
  supplierId,
  supplierName,
  materialName,
  sourceDocId = null,
  userId = null,
}) {
  const unit = row?.unit || "pcs"
  const quantity = Number(row?.amount ?? row?.quantity) || 0

  // For rolls with meters
  let adjustedQuantity = quantity
  let adjustedUnit = unit

  // If it's a roll type and we have originalMeters, use that
  if (row?.originalMeters && Number(row.originalMeters) > 0) {
    adjustedQuantity = Number(row.originalMeters)
    adjustedUnit = "m"
  }

  return await logWarehouseEvent({
    eventType: EVENT_TYPES.INCOMING,
    timestamp: arrivedAt,
    productTypeId,
    productTypeName,
    productName: row?.productName || null,
    materialId: row?.material || row?.materialId || null,
    materialName,
    unit: adjustedUnit,
    quantityChange: adjustedQuantity, // positive = incoming
    location: row?.location || row?.rollLocation || "Warehouse",
    rollWidth: row?.width || row?.materialWidth || row?.rollWidth || null,
    barcode: row?.barcode || null,
    ean: row?.ean || null,
    batchSeries: row?.batchSeries || null,
    price: row?.price || null,
    total: row?.total || null,
    metadata: {
      sourceCollection: "warehouseIncoming",
      sourceDocId,
      supplierId,
      supplierName,
      userId,
      notes: row?.notes || null,
      manufacturerCode: row?.manufacturerCode || null,
      core: row?.core || null,
    },
  })
}

/**
 * Log roll creation event
 */
export async function logRollCreatedEvent({ rollDoc, rollDocId, arrivedAt, userId = null }) {
  const meters = Number(rollDoc?.remainingMeters ?? rollDoc?.originalMeters) || 0

  return await logWarehouseEvent({
    eventType: EVENT_TYPES.ROLL_CREATED,
    timestamp: arrivedAt || new Date(),
    productTypeId: rollDoc?.productTypeId || null,
    productTypeName: rollDoc?.productTypeName || "Roll",
    productName: rollDoc?.productName || rollDoc?.material?.material || "Roll",
    materialId: rollDoc?.materialId || null,
    materialName: rollDoc?.material?.material || rollDoc?.material?.supplierMaterialName || null,
    unit: "m",
    quantityChange: meters, // positive
    location: rollDoc?.rollLocation || "Warehouse",
    rollWidth: rollDoc?.rollWidth || null,
    barcode: rollDoc?.barcode || null,
    ean: rollDoc?.ean || rollDoc?.material?.EAN || null,
    batchSeries: rollDoc?.batchSeries || null,
    price: rollDoc?.price || null,
    metadata: {
      sourceCollection: "rolls",
      sourceDocId: rollDocId,
      userId,
      notes: rollDoc?.comment || null,
      core: rollDoc?.core || null,
      weight: rollDoc?.weight || null,
    },
  })
}

/**
 * Log roll consumption (when meters are used)
 */
export async function logRollConsumedEvent({
  rollDoc,
  rollDocId,
  metersConsumed,
  orderId = null,
  userId = null,
}) {
  return await logWarehouseEvent({
    eventType: EVENT_TYPES.ROLL_CONSUMED,
    timestamp: new Date(),
    productTypeId: rollDoc?.productTypeId || null,
    productTypeName: rollDoc?.productTypeName || "Roll",
    productName: rollDoc?.productName || rollDoc?.material?.material || "Roll",
    materialId: rollDoc?.materialId || null,
    materialName: rollDoc?.material?.material || rollDoc?.material?.supplierMaterialName || null,
    unit: "m",
    quantityChange: -Math.abs(metersConsumed), // negative = consumed
    location: rollDoc?.rollLocation || "Warehouse",
    rollWidth: rollDoc?.rollWidth || null,
    barcode: rollDoc?.barcode || null,
    ean: rollDoc?.ean || rollDoc?.material?.EAN || null,
    metadata: {
      sourceCollection: "rolls",
      sourceDocId: rollDocId,
      orderId,
      userId,
    },
  })
}

/**
 * Log roll moved between locations
 */
export async function logRollMovedEvent({
  rollDoc,
  rollDocId,
  fromLocation,
  toLocation,
  userId = null,
}) {
  const meters = Number(rollDoc?.remainingMeters ?? rollDoc?.originalMeters) || 0

  // Log as outgoing from old location
  await logWarehouseEvent({
    eventType: EVENT_TYPES.ROLL_MOVED,
    timestamp: new Date(),
    productTypeId: rollDoc?.productTypeId || null,
    productTypeName: rollDoc?.productTypeName || "Roll",
    productName: rollDoc?.productName || rollDoc?.material?.material || "Roll",
    materialId: rollDoc?.materialId || null,
    materialName: rollDoc?.material?.material || rollDoc?.material?.supplierMaterialName || null,
    unit: "m",
    quantityChange: -meters, // negative from old location
    location: fromLocation,
    rollWidth: rollDoc?.rollWidth || null,
    barcode: rollDoc?.barcode || null,
    metadata: {
      sourceCollection: "rolls",
      sourceDocId: rollDocId,
      userId,
      movedTo: toLocation,
    },
  })

  // Log as incoming to new location
  return await logWarehouseEvent({
    eventType: EVENT_TYPES.ROLL_MOVED,
    timestamp: new Date(),
    productTypeId: rollDoc?.productTypeId || null,
    productTypeName: rollDoc?.productTypeName || "Roll",
    productName: rollDoc?.productName || rollDoc?.material?.material || "Roll",
    materialId: rollDoc?.materialId || null,
    materialName: rollDoc?.material?.material || rollDoc?.material?.supplierMaterialName || null,
    unit: "m",
    quantityChange: meters, // positive to new location
    location: toLocation,
    rollWidth: rollDoc?.rollWidth || null,
    barcode: rollDoc?.barcode || null,
    metadata: {
      sourceCollection: "rolls",
      sourceDocId: rollDocId,
      userId,
      movedFrom: fromLocation,
    },
  })
}

/**
 * Log inventory item creation (non-roll)
 */
export async function logInventoryCreatedEvent({
  inventoryDoc,
  inventoryDocId,
  arrivedAt = null,
  userId = null,
}) {
  const quantity = Number(inventoryDoc?.quantity ?? inventoryDoc?.amount) || 0

  return await logWarehouseEvent({
    eventType: EVENT_TYPES.INVENTORY_CREATED,
    timestamp: arrivedAt || new Date(),
    productTypeId: inventoryDoc?.productTypeId || null,
    productTypeName: inventoryDoc?.productTypeName || null,
    productName: inventoryDoc?.productName || null,
    materialId: inventoryDoc?.materialId || null,
    materialName: inventoryDoc?.materialName || inventoryDoc?.material || null,
    unit: inventoryDoc?.unit || "pcs",
    quantityChange: quantity, // positive
    location: inventoryDoc?.location || "Warehouse",
    barcode: inventoryDoc?.barcode || null,
    ean: inventoryDoc?.ean || null,
    batchSeries: inventoryDoc?.batchSeries || null,
    price: inventoryDoc?.price || null,
    total: inventoryDoc?.total || null,
    metadata: {
      sourceCollection: "warehouseInventory",
      sourceDocId: inventoryDocId,
      supplierId: inventoryDoc?.supplierId || null,
      supplierName: inventoryDoc?.supplierName || null,
      userId,
      notes: inventoryDoc?.notes || null,
    },
  })
}

/**
 * Log inventory adjustment (when quantity changes)
 */
export async function logInventoryAdjustmentEvent({
  inventoryDoc,
  inventoryDocId,
  oldQuantity,
  newQuantity,
  reason = null,
  userId = null,
}) {
  const change = Number(newQuantity) - Number(oldQuantity)

  return await logWarehouseEvent({
    eventType: EVENT_TYPES.INVENTORY_ADJUSTMENT,
    timestamp: new Date(),
    productTypeId: inventoryDoc?.productTypeId || null,
    productTypeName: inventoryDoc?.productTypeName || null,
    productName: inventoryDoc?.productName || null,
    materialId: inventoryDoc?.materialId || null,
    materialName: inventoryDoc?.materialName || inventoryDoc?.material || null,
    unit: inventoryDoc?.unit || "pcs",
    quantityChange: change,
    location: inventoryDoc?.location || "Warehouse",
    barcode: inventoryDoc?.barcode || null,
    ean: inventoryDoc?.ean || null,
    metadata: {
      sourceCollection: "warehouseInventory",
      sourceDocId: inventoryDocId,
      userId,
      reason,
      oldQuantity: Number(oldQuantity),
      newQuantity: Number(newQuantity),
    },
  })
}

/**
 * Log inventory deletion
 */
export async function logInventoryDeletedEvent({ inventoryDoc, inventoryDocId, userId = null }) {
  const quantity = Number(inventoryDoc?.quantity ?? inventoryDoc?.amount) || 0

  return await logWarehouseEvent({
    eventType: EVENT_TYPES.INVENTORY_DELETED,
    timestamp: new Date(),
    productTypeId: inventoryDoc?.productTypeId || null,
    productTypeName: inventoryDoc?.productTypeName || null,
    productName: inventoryDoc?.productName || null,
    materialId: inventoryDoc?.materialId || null,
    materialName: inventoryDoc?.materialName || inventoryDoc?.material || null,
    unit: inventoryDoc?.unit || "pcs",
    quantityChange: -Math.abs(quantity), // negative = removed
    location: inventoryDoc?.location || "Warehouse",
    barcode: inventoryDoc?.barcode || null,
    ean: inventoryDoc?.ean || null,
    metadata: {
      sourceCollection: "warehouseInventory",
      sourceDocId: inventoryDocId,
      userId,
      notes: inventoryDoc?.notes || null,
    },
  })
}

/**
 * Log roll deletion
 */
export async function logRollDeletedEvent({ rollDoc, rollDocId, userId = null }) {
  const meters = Number(rollDoc?.remainingMeters ?? rollDoc?.originalMeters) || 0

  return await logWarehouseEvent({
    eventType: EVENT_TYPES.ROLL_DELETED,
    timestamp: new Date(),
    productTypeId: rollDoc?.productTypeId || null,
    productTypeName: rollDoc?.productTypeName || "Roll",
    productName: rollDoc?.productName || rollDoc?.material?.material || "Roll",
    materialId: rollDoc?.materialId || null,
    materialName: rollDoc?.material?.material || rollDoc?.material?.supplierMaterialName || null,
    unit: "m",
    quantityChange: -Math.abs(meters), // negative = removed
    location: rollDoc?.rollLocation || "Warehouse",
    rollWidth: rollDoc?.rollWidth || null,
    barcode: rollDoc?.barcode || null,
    ean: rollDoc?.ean || rollDoc?.material?.EAN || null,
    metadata: {
      sourceCollection: "rolls",
      sourceDocId: rollDocId,
      userId,
      notes: rollDoc?.comment || null,
    },
  })
}
